import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Users, FileText, ArrowRight, Clock } from "lucide-react";
import AnalyticsCard from "@/components/AnalyticsCard";
import { serviceLabel } from "@/components/NewVisitModal";

function StatBox({ label, value, tone = "navy", sub, testid }) {
  const t = {
    navy: "bg-brand-navy text-white",
    azure: "bg-brand-azure text-white",
    orange: "bg-brand-orange text-white",
    light: "bg-white text-brand-navy border border-[#E2E8F0]",
  };
  return (
    <div className={`${t[tone]} rounded-xl p-5 min-w-0`} data-testid={testid}>
      <div className="text-xs uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-2xl sm:text-3xl font-extrabold mt-1.5 tabular-nums break-words">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function QuickAction({ icon: Icon, label, sub, onClick, testid, tone = "white" }) {
  const cls = tone === "orange"
    ? "bg-brand-orange hover:bg-brand-orangeDark text-white border-transparent"
    : "bg-white hover:border-brand-azure text-brand-navy border-[#E2E8F0]";
  return (
    <button data-testid={testid} onClick={onClick} className={`${cls} border-2 rounded-xl p-4 text-left transition flex items-center gap-3 w-full`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tone === "orange" ? "bg-white/20" : "bg-brand-azure/10 text-brand-azure"}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-extrabold">{label}</div>
        <div className={`text-xs truncate ${tone === "orange" ? "text-white/80" : "text-slate-500"}`}>{sub}</div>
      </div>
      <ArrowRight size={16} className="shrink-0 opacity-60" />
    </button>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [todayCount, setTodayCount] = useState(null);
  const [sites, setSites] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/medico/dashboard-stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/medico/sites").then(({ data }) => setSites((data || []).filter((s) => s.active !== false))).catch(() => {});
    const d = new Date().toISOString().slice(0, 10);
    api.get("/visits", { params: { date_from: d, date_to: d } }).then(({ data }) => setTodayCount((data || []).length)).catch(() => setTodayCount(0));
  }, []);

  return (
    <AppShell title="Home" subtitle="Panoramica dello studio: metriche, prossimi appuntamenti e scorciatoie">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="home-quick-actions">
        <QuickAction icon={Plus} label="Nuova visita" sub="Apri una nuova cartella clinica" tone="orange" testid="home-action-new-visit" onClick={() => nav("/visite?new=1")} />
        <QuickAction icon={Clock} label="Visite di oggi" sub={todayCount === null ? "…" : `${todayCount} in lista oggi`} testid="home-action-visite" onClick={() => nav("/visite")} />
        <QuickAction icon={Calendar} label="Agenda" sub="Calendario appuntamenti" testid="home-action-agenda" onClick={() => nav("/agenda")} />
        <QuickAction icon={Users} label="Pazienti" sub="Anagrafiche e storico" testid="home-action-pazienti" onClick={() => nav("/pazienti")} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="home-stats">
        <StatBox label="Bozze aperte" value={stats?.drafts ?? "—"} tone="orange" testid="home-stat-drafts" />
        <StatBox label="Visite chiuse" value={stats?.closed ?? "—"} tone="navy" testid="home-stat-closed" />
        <StatBox label="Referti inviati" value={stats?.reports_sent ?? "—"} tone="azure" testid="home-stat-reports" />
        {stats?.next_appointment ? (
          <StatBox
            label="Prossima visita"
            tone="light"
            value={stats.next_appointment.patient_full_name || "Appuntamento programmato"}
            sub={`${serviceLabel(stats.next_appointment.type || stats.next_appointment.service)} · ${new Date(stats.next_appointment.starts_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
            testid="stat-next-appointment"
          />
        ) : (
          <StatBox
            label="Ultima visita"
            tone="light"
            value={stats?.last_visit?.patient_full_name || "—"}
            sub={
              stats?.last_visit
                ? `${serviceLabel(stats.last_visit.service)} · ${new Date(stats.last_visit.closed_at || stats.last_visit.report_sent_at || stats.last_visit.created_at).toLocaleDateString("it-IT")}`
                : "Nessuna visita effettuata"
            }
            testid="stat-last-visit"
          />
        )}
      </div>
      {stats?.next_appointment && stats?.last_visit && (
        <div className="text-xs text-slate-500 mb-4 -mt-3" data-testid="stat-last-visit-secondary">
          Ultima visita effettuata: <b className="text-brand-navy">{stats.last_visit.patient_full_name}</b> · {serviceLabel(stats.last_visit.service)} · {new Date(stats.last_visit.closed_at || stats.last_visit.report_sent_at || stats.last_visit.created_at).toLocaleDateString("it-IT")}
        </div>
      )}

      <AnalyticsCard sedi={sites} />

      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="home-storico-hint">
        <div className="flex items-start gap-3 min-w-0">
          <FileText className="text-brand-azure shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-slate-600">Cerchi una visita passata? Lo storico completo con i filtri è nella sezione <b>Pazienti → Tutte le visite</b>.</div>
        </div>
        <button data-testid="home-goto-storico" onClick={() => nav("/pazienti?tab=visite")} className="text-brand-azure font-bold text-sm hover:underline shrink-0 text-left sm:text-right">Vai allo storico</button>
      </div>
    </AppShell>
  );
}
